"use client";

import React, { useState } from "react";
import { useCart } from "@/lib/hooks/useCart";
import type {
  PriceRangeTableProps,
  PriceRangeModalContentProps,
} from "@/lib/models/priceRangeTable";
import type { PriceSectionEntry } from "@/lib/models/product";
import { ShoppingCart, Calendar } from "lucide-react";
import { Button } from "./button";
import { QuantitySelector } from "./QuantitySelector";
import { cn } from "@/lib/utils";
import {
  formatValidityDate,
  getSectionValidityEndDate,
} from "@/lib/utils/generatePrice";

export function PriceRangeTable({
  product,
  currentQuantity,
}: PriceRangeTableProps) {
  const { productKey, formatPrice } = useCart();

  const rangosSection = product?.price?.secciones?.find(
    (s) =>
      s.tipo === "PRECIOS_POR_PERIODO_Y_CANTIDAD_RANGOS" ||
      s.tipo === "PRECIOS_POR_PERIODO_Y_CANTIDAD_SIN_RANGOS",
  );

  const entradas = rangosSection?.entradas || [];

  if (!rangosSection || entradas.length === 0) {
    return (
      <div className="py-6 px-4 text-center text-slate-500 font-medium">
        <p className="text-sm">
          Este producto no cuenta con precios por escala de volumen.
        </p>
      </div>
    );
  }

  const validityEndDate = getSectionValidityEndDate(rangosSection);

  // Ordenar las entradas por cantidad mínima ascendente
  const sortedEntradas = [...entradas].sort(
    (a, b) => (a.cantidadMinima ?? 0) - (b.cantidadMinima ?? 0),
  );

  const getPriceVal = (entry: PriceSectionEntry): number => {
    return (
      (productKey === "precioMxn"
        ? entry.precioUnitarioMxn
        : entry.precioUnitarioUsd) ||
      (productKey === "precioMxn" ? entry.precioMxn : entry.precioUsd) ||
      0
    );
  };

  return (
    <div className="w-full flex flex-col gap-2">
      {validityEndDate && (
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-600 self-start">
          <Calendar className="w-3.5 h-3.5 shrink-0" />
          <span>Vigencia hasta: {formatValidityDate(validityEndDate)}</span>
        </div>
      )}
      <div className="w-full overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-xs">
      <table className="w-full border-collapse text-left text-xs sm:text-sm text-slate-600">
        <thead className="bg-slate-50 text-[11px] sm:text-xs uppercase font-bold text-slate-700 border-b border-slate-200">
          <tr>
            <th scope="col" className="px-3 sm:px-4 py-2.5 whitespace-nowrap">
              Cant. mínima
            </th>
            <th scope="col" className="px-3 sm:px-4 py-2.5 whitespace-nowrap">
              Cant. máxima
            </th>
            <th scope="col" className="px-3 py-2.5 text-center whitespace-nowrap">
              Descuento
            </th>
            <th scope="col" className="px-3 sm:px-4 py-2.5 text-right whitespace-nowrap">
              Precio unitario
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sortedEntradas.map((entry, index) => {
            const minQty = entry.cantidadMinima ?? 1;
            const maxQty = entry.cantidadMaxima ?? 999999;
            const isMaxInfinite = maxQty >= 999999;
            const isActive =
              currentQuantity !== undefined &&
              currentQuantity >= minQty &&
              currentQuantity <= maxQty;
            const unitPrice = getPriceVal(entry);

            return (
              <tr
                key={index}
                className={cn(
                  "transition-colors duration-150",
                  isActive
                    ? "bg-orange-50/90 text-orange-950 font-semibold ring-1 ring-inset ring-orange-300"
                    : "hover:bg-slate-50/70 text-slate-700",
                )}
              >
                <td className="px-3 sm:px-4 py-2.5 font-semibold">
                  <span className="tabular-nums">{minQty}</span>
                </td>
                <td className="px-3 sm:px-4 py-2.5 font-medium text-slate-600">
                  <span className="tabular-nums">
                    {isMaxInfinite ? "En adelante" : maxQty}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-center">
                  {entry.porcentajeDescuento ? (
                    <span
                      className={cn(
                        "inline-block px-2 py-0.5 rounded-full text-xs font-bold tabular-nums transition-colors",
                        isActive
                          ? "bg-orange-600 text-white"
                          : "bg-emerald-50 text-emerald-700 border border-emerald-200/60",
                      )}
                    >
                      {entry.porcentajeDescuento}%
                    </span>
                  ) : (
                    <span className="text-slate-400 font-normal">-</span>
                  )}
                </td>
                <td className="px-3 sm:px-4 py-2.5 text-right font-bold text-slate-900 tabular-nums whitespace-nowrap">
                  <span>{formatPrice(unitPrice)}</span>
                  <span className="text-[10px] text-slate-500 font-normal ml-1">
                    + IVA
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </div>
  );
}

export function PriceRangeModalContent({
  product,
  initialQuantity,
  totalStock,
  onAddToCart,
}: PriceRangeModalContentProps) {
  const [selectedQuantity, setSelectedQuantity] = useState<number>(
    initialQuantity > 0 ? initialQuantity : 1,
  );

  const handleAddToCart = () => {
    onAddToCart(selectedQuantity);
  };

  return (
    <div className="flex flex-col gap-4 py-1">
      {/* Información del producto */}
      <div className="pb-2 border-b border-slate-100">
        <p className="text-xs text-slate-500 font-medium">
          {product.sku} {product.marcaName ? `| ${product.marcaName}` : ""}
        </p>
        <h4 className="text-sm sm:text-base font-bold text-slate-900 line-clamp-2">
          {product.name}
        </h4>
      </div>

      <p className="text-xs text-slate-500">
        Los precios por escala aplican automáticamente según el volumen de compra seleccionado.
      </p>

      {/* Tabla dinámica de escalas de precios */}
      <PriceRangeTable product={product} currentQuantity={selectedQuantity} />

      {/* Selector de cantidad interactivo y botón Agregar al carrito */}
      <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-xs font-semibold text-slate-700 whitespace-nowrap">
            Cantidad:
          </span>
          <QuantitySelector
            quantity={selectedQuantity}
            min={1}
            max={totalStock > 0 ? totalStock : 1}
            onChange={setSelectedQuantity}
            disabled={totalStock <= 0}
            className="w-28 sm:w-32"
          />
        </div>

        <Button
          type="button"
          onClick={handleAddToCart}
          disabled={totalStock <= 0}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 h-9 text-xs sm:text-sm font-semibold"
        >
          <ShoppingCart size={15} />
          <span>Agregar al carrito</span>
        </Button>
      </div>
    </div>
  );
}
